import { LoginStep } from "@heidelbergmaterials/fcl-authclient"
import { Loading } from "@hm/fundamental"
import { useAuth } from "@shared/lib/auth"
import React from "react"
import { Navigate } from "react-router-dom"

import { ContainerBox } from "../.."
import type { RouteGuardProps } from "../model"
import { hasRequiredPermissions, filterValidPermissions } from "../model"

/**
 * RouteGuard component that protects routes based on authentication and permissions
 *
 * @example
 * // Protect route with authentication only
 * <RouteGuard>
 *   <ProtectedPage />
 * </RouteGuard>
 *
 * @example
 * // Protect route with specific permissions (OR logic - at least one)
 * <RouteGuard requiredPermissions={[PermissionType.Admin, PermissionType.Developer]}>
 *   <AdminPage />
 * </RouteGuard>
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({
	children,
	requiredPermissions = [],
}) => {
	const { loginStep, userPermissions } = useAuth()

	// Check authentication
	if (loginStep === LoginStep.Unauthenticated) {
		return <Navigate to="/unauthorized" replace />
	}

	// If no specific permissions required, grant access
	if (!requiredPermissions || requiredPermissions.length === 0) {
		return <>{children}</>
	}

	// Show loading state while permissions are being fetched
	// This prevents premature redirects when permissions haven't loaded yet
	if (userPermissions === undefined) {
		return (
			<ContainerBox data-cy="loading-page">
				<Loading size="xlarge" className="block mt-6 mx-auto" />
			</ContainerBox>
		)
	}

	// Filter out expired permissions
	const validPermissions = filterValidPermissions(userPermissions)

	// Check if user has required permissions (OR logic)
	const hasPermissions = hasRequiredPermissions(
		validPermissions,
		requiredPermissions,
	)

	if (!hasPermissions) {
		return <Navigate to="/access-denied" replace />
	}

	return <>{children}</>
}
